import { Button } from "../common/Button";
import { JournalBody } from "./JournalBody";

/**
 * A long entry that does not bury every entry after it (#473).
 *
 * The body cap is 20,000 characters -- roughly 3,000 to 4,000 words. One
 * maxed-out entry rendered in full makes scrolling past it the only way
 * to reach anything else, which is the opposite of what the filters in
 * #464 were added to fix.
 *
 * ── CLAMPED, NOT CUT ─────────────────────────────────────────────────
 *
 * max-height with a fade, rather than truncating the text. A sentence
 * severed mid-word reads as data loss, and on a private journal that is
 * an alarming thing to appear to have happened. Nothing is removed: the
 * whole entry is in the DOM, the overflow is simply not shown.
 *
 * That also keeps the browser's own find-in-page working on the hidden
 * part, which a truncating implementation would silently break.
 *
 * ── PRESENTATION ONLY ────────────────────────────────────────────────
 *
 * This component knows nothing about search or filtering. Server-side
 * search (#466) matches the stored body regardless of what is painted,
 * so a word only visible when expanded is still findable -- which is
 * the behaviour somebody would assume and the one most likely to be
 * broken by a clever implementation.
 *
 * ── EXPANSION LIVES IN THE PARENT ────────────────────────────────────
 *
 * `expanded` and `onToggle` are passed in rather than held here, because
 * the list RELOADS after a tag, colour or pin change. State owned by
 * this component would survive a re-render but not a remount, so an
 * entry somebody expanded would silently collapse underneath them when
 * they ticked a tag -- a small betrayal, and a confusing one, since
 * nothing they did was about the text.
 */

/* Roughly twenty lines. Deliberately generous: most entries are far
   shorter than this and should never show the control at all, so the
   clamp only appears where the wall actually forms. */
const CLAMP_PX = 420;

export function CollapsibleBody({ body, expanded, onToggle, onToggleCheckbox }) {
  /* An estimate, not a measurement. useLayoutEffect against scrollHeight
     would be exact and would also run on every row of every list render
     to answer a question that only matters for the few long ones. The
     cost of being wrong is a "Show more" on an entry that did not need
     one, which is a far smaller problem than a janky list. */
  const long = (body || "").length > 1200;

  if (!long) {
    return <JournalBody body={body} onToggle={onToggleCheckbox} />;
  }

  return (
    <div>
      <div
        style={{
          maxHeight: expanded ? "none" : CLAMP_PX,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <JournalBody body={body} onToggle={onToggleCheckbox} />
        {!expanded && (
          /* The fade says "there is more" without a line of copy. Sits
             above the text and ignores clicks, so it never swallows a
             checkbox underneath it. */
          <div
            aria-hidden="true"
            style={{
              position: "absolute", left: 0, right: 0, bottom: 0, height: 64,
              background: "linear-gradient(to bottom, transparent, var(--color-surface))",
              pointerEvents: "none",
            }}
          />
        )}
      </div>
      <Button size="sm" variant="secondary" onClick={onToggle}>
        {expanded ? "Show less" : "Show more"}
      </Button>
    </div>
  );
}
